import { ArrowRight, Github, Server, Bot, Rocket } from "lucide-react";
import { Button } from "../ui/button";
import { MarketingLink } from "@/components/OptionalLink";
import useSWR from "swr";
import { JSONSafe, User } from "@/types";
import { fetcherIgnore404, cn } from "@/lib/utils";
import HeroIcons from "@/components/landing/HeroIcons";
import { ShineBorder } from "@/components/magicui/shine-border";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import Link from "next/link";

const ValueCard = ({
  icon,
  title,
  children,
  shine,
  onMouseEnter,
  onMouseLeave,
  user
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  shine?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  user?: JSONSafe<User>;
}) => (
  <Link
    className="relative z-20 flex items-start max-w-[550px] gap-4 border rounded-md p-4 bg-muted/60 cursor-pointer"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    href={user ? "/dashboard" : "/auth/signUp?callbackUrl=/dashboard"}
  >
    {shine && <ShineBorder duration={10} shineColor={["#A07CFE", "#d53f73", "#FFBE7B"]} />}
    <div>{icon}</div>
    <div>
      <h3 className="font-semibold text-xl select-none">{title}</h3>
      <p className="text-muted-foreground mt-1 text-base select-none">{children}</p>
    </div>
  </Link>
);

const cards = [
  {
    icon: <Github className="w-8 h-8" />,
    title: "GitHub to Production",
    children: "Push code. AI configures everything. Your app goes live."
  },
  {
    icon: <Server className="w-8 h-8" />,
    title: "Use Any Server",
    children: "Add your server or provision a new one in one click. You own it, we manage it."
  },
  {
    icon: <Rocket className="w-8 h-8" />,
    title: "Batteries Included",
    children: "SSL, monitoring, logs — all handled. Your app runs like it did locally."
  },
  {
    icon: <Bot className="w-8 h-8" />,
    title: "AI Agent, At Your Command",
    children:
      "Ask to deploy, provision, or debug production. Your AI agent acts — it doesn't just advise."
  }
];

export const Hero = () => {
  const { data: user } = useSWR<JSONSafe<User>>("/api/user", fetcherIgnore404);
  const refContainer = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1);
  const [textClass, setTextClass] = useState<string | undefined>(undefined);

  useResizeObserver<HTMLDivElement>({
    ref: refContainer as RefObject<HTMLDivElement>,
    box: "border-box",
    onResize: function (size) {
      // Only scale on the lg breakpoint and above
      if (size.width && size.width > 900) {
        if (size.height && size.height < 560) {
          const newScale = size.height / 540;
          setScale(newScale);
          if (newScale > 0.8) {
            setTextClass(undefined);
          } else if (newScale > 0.6) {
            setTextClass("text-3xl mb-2");
          } else {
            setTextClass("text-2xl");
          }
        } else {
          setScale(1);
        }
      } else {
        setScale(1);
        setTextClass(undefined);
      }
    }
  });

  return (
    <section
      ref={refContainer}
      id="hero"
      className="lg:h-[calc(100vh-60px)] mt-20 lg:mt-0 container gap-20 lg:gap-5 flex flex-col lg:flex-row items-center justify-center lg:justify-between px-4 xl:px-20"
    >
      <div className="flex flex-col gap-2 items-center lg:items-start">
        <h1
          className={cn(
            "font-bold tracking-tighter text-balance text-center lg:text-left",
            textClass ? textClass : "text-4xl md:text-5xl lg:text-6xl mb-4"
          )}
        >
          Your App. Your Server.{" "}
          <span className="bg-gradient-to-r from-blue-500 via-sky-500 to-blue-600 text-transparent bg-clip-text">
            Zero DevOps.
          </span>
        </h1>

        <p className="text-center mb-4 lg:text-left text-lg md:text-xl text-muted-foreground text-balance">
          <b className="text-foreground">DollarDeploy AI</b> will deploy your app, spin up a
          server, or fix a production issue. No DevOps expertise needed.
        </p>

        <div className="flex justify-center lg:justify-start w-full mb-6">
          <HeroIcons className="w-[300px] p-0" />
        </div>

        <div className="flex justify-center lg:justify-start w-full">
          <Button size="lg" asChild>
            <MarketingLink
              href={user ? "/dashboard" : "/auth/signUp?callbackUrl=/dashboard"}
              className="w-50 flex items-center"
            >
              <span className="ml-2">Start now</span> <ArrowRight className="h-5 w-5 mr-2" />
            </MarketingLink>
          </Button>
        </div>
      </div>

      <HeroCards cards={cards} scale={scale} user={user} />
    </section>
  );
};

const HeroCards = ({
  cards,
  scale,
  user
}: {
  cards: { icon: React.ReactNode; title: string; children: string }[];
  scale: number;
  user?: JSONSafe<User>;
}) => {
  const [shineIndex, setShineIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState<number | undefined>(undefined);

  useEffect(() => {
    const interval = setInterval(() => {
      setShineIndex(prev => (prev + 1) % cards.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [cards.length]);

  const handleMouseEnter = useCallback((index: number) => {
    setHoverIndex(index);
  }, []);

  const handleMouseLeave = useCallback((index: number) => {
    setHoverIndex(undefined);
    setShineIndex(index);
  }, []);

  return (
    <div
      className="relative flex valuePropositions flex-col gap-4 rounded-xl"
      style={scale !== 1 ? { transform: `scale(${scale})` } : undefined}
    >
      <div
        className="absolute left-10 top-10 w-full h-full scale-[0.5] md:scale-[0.75] lg:scale-100"
        aria-hidden="true"
      >
        <div className="shadow"></div>
      </div>

      {cards.map((card, index) => (
        <ValueCard
          key={card.title}
          icon={card.icon}
          title={card.title}
          shine={hoverIndex !== undefined ? hoverIndex === index : shineIndex === index}
          user={user}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={() => handleMouseLeave(index)}
        >
          {card.children}
        </ValueCard>
      ))}
    </div>
  );
};
